import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, of, take } from 'rxjs';

import { UserRouteV1Model } from '@core/models/entities/implementations/user-route-v1';
import { UserVehicleV1Model } from '@core/models/entities/implementations/user-vehicle-v1';
import {
  PlanFilter,
  PlanFilterOperator
} from '@core/models/entity/entity-selector-request.model';
import { RouteResources } from '@core/models/plan-edit-gesture.model';
import { ICONS } from '@core/services/icons/icons.service';
import { SelectorsService } from '@core/services/selectors/selectors.service';
import { selectTimelineVisualization } from '@core/store/data-visualization/data-visualization.selectors';
import { selectDataspaceState } from '@core/store/dataspace/dataspace.selectors';
import { PlanEditSessionActions } from '@core/store/plan-edit/plan-edit-session.actions';
import { selectPlanWatchForView } from '@core/store/plan-edit/plan-edit-session.selectors';
import { selectPlan } from '@core/store/plan/plan.selectors';
import { isRouteRef } from '@core/utils/plan-edit/targets';

import { SelectItem, SelectValue } from '@shared/components/select/select-item.model';
import { SelectComponent } from '@shared/components/select/select.component';

import { EntityAutoCompleteService } from '@features/components/entities/entity-auto-complete-dialog/entity-auto-complete.service';

import { KnownRouteResources, knownRouteResources } from '../known-route-resources';
import { ResourceKind, injectResourceNames } from '../resource-names';

type ResourceKey = 'vehicleUid' | 'driverUid' | 'trailerUid';

interface ClearedNote {
  key: string;
  params: Record<string, string>;
}

const ROWS: { key: ResourceKey; kind: ResourceKind; icon: string }[] = [
  { key: 'vehicleUid', kind: 'vehicle', icon: ICONS.truck },
  { key: 'driverUid', kind: 'driver', icon: ICONS.user },
  { key: 'trailerUid', kind: 'trailer', icon: ICONS.truckTrailer }
];

/** Swaps the ticked resources between two routes, in one gesture. */
@Component({
  selector: 'app-swap-resources-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, SelectComponent],
  templateUrl: './swap-resources-form.component.html',
  styles: ``
})
export class SwapResourcesFormComponent {
  readonly routeUid = input.required<string>();
  readonly otherRouteUid = input<string | null>(null);
  readonly done = output<void>();

  private readonly store = inject(Store);
  private readonly picker = inject(EntityAutoCompleteService);
  private readonly injector = inject(Injector);
  private readonly selectors = inject(SelectorsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dataspaceState = this.store.selectSignal(selectDataspaceState);
  private readonly plan = this.store.selectSignal(selectPlan);
  private readonly timeline = this.store.selectSignal(selectTimelineVisualization);
  private readonly watch = this.store.selectSignal(selectPlanWatchForView);

  protected readonly ICONS = ICONS;
  protected readonly rows = ROWS;

  /** Routes picked through the reference picker that neither the timeline nor the overlay holds. */
  private readonly fetchedRoutes = signal<Record<string, UserRouteV1Model>>({});
  private readonly requestedRoutes = new Set<string>();

  private readonly known = computed(() =>
    knownRouteResources(this.timeline().rows, {
      ...this.fetchedRoutes(),
      ...((this.watch().overlay.upserted['UserRoute'] ?? {}) as unknown as Record<
        string,
        UserRouteV1Model
      >)
    })
  );

  protected readonly a = computed(() => this.known().get(this.routeUid()));
  protected readonly chosenOtherUid = linkedSignal(() => this.otherRouteUid() ?? '');
  protected readonly b = computed(() => this.known().get(this.chosenOtherUid()));

  protected readonly resourceName = injectResourceNames(
    computed(() =>
      [this.a(), this.b()].flatMap((known) =>
        known ? ROWS.map((row) => ({ kind: row.kind, uid: known[row.key] })) : []
      )
    )
  );

  protected readonly validatedRoute = computed(
    () => !!(this.a()?.validated || this.b()?.validated)
  );

  protected readonly routeItems = computed<SelectItem[]>(() => {
    const items: SelectItem[] = [...this.known().values()]
      .filter((known) => known.routeUid !== this.routeUid() && !isRouteRef(known.routeUid))
      .map((known) => ({
        id: known.routeUid,
        label: known.name,
        icon: ICONS.path,
        disabled: known.validated
      }));
    const picked = this.chosenOtherUid();
    if (picked && !this.known().has(picked))
      items.push({ id: picked, label: picked, icon: ICONS.path });
    return items;
  });

  protected readonly ticked = signal<ReadonlySet<ResourceKey>>(new Set(['vehicleUid']));

  protected readonly result = computed<{ a: RouteResources; b: RouteResources } | null>(
    () => {
      const a = this.a();
      const b = this.b();
      if (!a || !b || a.validated || b.validated || this.ticked().size === 0) return null;
      const swapped = (
        own: KnownRouteResources,
        other: KnownRouteResources
      ): RouteResources => ({
        routeUid: own.routeUid,
        vehicleUid: this.ticked().has('vehicleUid') ? other.vehicleUid : own.vehicleUid,
        driverUid: this.ticked().has('driverUid') ? other.driverUid : own.driverUid,
        trailerUid: this.ticked().has('trailerUid') ? other.trailerUid : own.trailerUid
      });
      return { a: swapped(a, b), b: swapped(b, a) };
    }
  );

  /** Each loaded vehicle's own company, by uid; `null` for a shared-pool vehicle. */
  private readonly vehicleCompanies = signal<ReadonlyMap<string, string | null>>(new Map());
  private readonly requestedVehicles = new Set<string>();

  /**
   * The routes whose set company the vehicle they take does not work for. DL refuses such a swap,
   * so it clears their company in the same batch; a vehicle not loaded yet clears nothing, and a
   * refusal then offers the retry.
   */
  protected readonly clearedRoutes = computed<KnownRouteResources[]>(() => {
    const a = this.a();
    const b = this.b();
    if (!a || !b || !this.ticked().has('vehicleUid')) return [];
    return [
      [a, b],
      [b, a]
    ]
      .filter(([own, other]) => {
        const incoming = this.vehicleCompanies().get(other.vehicleUid);
        return !!own.companyUid && !!incoming && incoming !== own.companyUid;
      })
      .map(([own]) => own);
  });

  protected readonly clearedNote = computed((): ClearedNote | null => {
    const [first, second] = this.clearedRoutes();
    if (!first) return null;
    if (!second) {
      return {
        key: 'features.plan.edit.swap.company_cleared',
        params: { route: first.name }
      };
    }
    return {
      key: 'features.plan.edit.swap.company_cleared_both',
      params: { first: first.name, second: second.name }
    };
  });

  constructor() {
    effect(() => {
      const uid = this.chosenOtherUid();
      const dataSpaceId = this.dataspaceState()?.dataspace?.id;
      if (!uid || dataSpaceId === undefined || this.requestedRoutes.has(uid)) return;
      if (this.known().has(uid)) return;
      this.requestedRoutes.add(uid);
      this.selectors
        .selectEntityByUid$('ROUTE', 'UserRoute', uid, dataSpaceId, this.planFilter())
        .pipe(
          take(1),
          catchError(() => of(undefined)),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((entity) => {
          if (!entity) return;
          this.fetchedRoutes.update((routes) => ({
            ...routes,
            [uid]: entity.raw as UserRouteV1Model
          }));
        });
    });
    effect(() => {
      const dataSpaceId = this.dataspaceState()?.dataspace?.id;
      if (dataSpaceId === undefined) return;
      const planFilter = this.planFilter();
      for (const known of [this.a(), this.b()]) {
        const uid = known?.vehicleUid;
        if (!uid || this.requestedVehicles.has(uid)) continue;
        this.requestedVehicles.add(uid);
        this.selectors
          .selectEntityByUid$('VEHICLE', 'UserVehicle', uid, dataSpaceId, planFilter)
          .pipe(
            take(1),
            catchError(() => of(undefined)),
            takeUntilDestroyed(this.destroyRef)
          )
          .subscribe((entity) => {
            if (!entity) return;
            const company = (entity.raw as UserVehicleV1Model).company_uid ?? null;
            this.vehicleCompanies.update((known) => new Map(known).set(uid, company));
          });
      }
    });
  }

  private planFilter(): PlanFilter | undefined {
    const planId = this.plan()?.id;
    return planId ? { id: planId, operator: PlanFilterOperator.IN } : undefined;
  }

  protected onRoute(value: SelectValue): void {
    if (typeof value === 'string') this.chosenOtherUid.set(value);
  }

  protected toggle(key: ResourceKey): void {
    this.ticked.update((ticked) => {
      const next = new Set(ticked);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }

  protected pickOther(): void {
    const picked = signal<string | string[] | null>(null);
    this.picker.show('route', picked, [], false);
    const watcher = effect(
      () => {
        const value = picked();
        if (typeof value === 'string') {
          this.chosenOtherUid.set(value);
          watcher.destroy();
        } else if (!this.picker.isVisible()) {
          watcher.destroy();
        }
      },
      { injector: this.injector }
    );
  }

  protected swap(): void {
    const result = this.result();
    if (!result) return;
    const clears = this.clearedRoutes().map((known) => known.routeUid);
    this.store.dispatch(
      PlanEditSessionActions.gestureRequested({
        gesture: {
          kind: 'swap-resources',
          ...result,
          ...(clears.length > 0 ? { clearsCompanyOf: clears } : {})
        }
      })
    );
    this.done.emit();
  }
}
